import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { getCurrentUser } from "../../services/auth";

interface TourStep {
 id: string;
 target?: string; // CSS selector for element to highlight
 title: string;
 content: string;
 position?:"top" | "bottom" | "left" | "right";
}

/**
 * Tour steps for first-time users
 */
const TOUR_STEPS: TourStep[] = [
 {
 id: "welcome",
 title: "Welcome to Time Tracking!",
 content: "Let's take a quick tour to help you get started. This will only take a minute.",
 },
 {
 id: "dashboard",
 target: "[data-tour='dashboard']",
 title: "Your Dashboard",
 content: "This is your home base. See your daily stats, pending tasks, and quick actions all in one place.",
 position: "bottom",
 },
 {
 id: "sidebar",
 target: "[data-tour='sidebar']",
 title: "Navigation",
 content: "Use the sidebar to navigate between Projects, Tasks, Timesheets, and more.",
 position: "right",
 },
 {
 id: "search",
 target: "[data-tour='search']",
 title: "Quick Search",
 content: "Press Cmd+K (or Ctrl+K) anytime to quickly search and navigate anywhere in the app.",
 position: "bottom",
 },
 {
 id: "help",
 target: "[data-tour='help']",
 title: "Need Help?",
 content: "Click the help button anytime to see tips for the current page and a glossary of terms.",
 position: "left",
 },
 {
 id: "complete",
 title: "You're All Set!",
 content: "You can always restart this tour from Settings. Enjoy using Time Tracking!",
 },
];

const TOUR_STORAGE_KEY ="time-tracking-tour-completed";

/**
 * Check if user has completed the tour
 */
export function hasCompletedTour(): boolean {
 return localStorage.getItem(TOUR_STORAGE_KEY) ==="true";
}

/**
 * Mark tour as completed
 */
export function completeTour(): void {
 localStorage.setItem(TOUR_STORAGE_KEY,"true");
}

/**
 * Reset tour (for testing or settings)
 */
export function resetTour(): void {
 localStorage.removeItem(TOUR_STORAGE_KEY);
}

/**
 * OnboardingTour - Guided tour for new users
 * Only shows after user is logged in (not on login/auth pages)
 */
export function OnboardingTour({ onComplete }: { onComplete?: () => void }) {
 const [isActive, setIsActive] = useState(false);
 const [currentStep, setCurrentStep] = useState(0);
 const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
 const location = useLocation();

 const step = TOUR_STEPS[currentStep];
 const isFirstStep = currentStep === 0;
 const isLastStep = currentStep === TOUR_STEPS.length - 1;

 // Pages where tour should NOT show (auth pages)
 const authPages = ["/","/login","/forgot-password","/reset-password","/magic-link","/already-logged-in","/change-password",
 ];
 const isAuthPage = authPages.includes(location.pathname);

 // Check if should show tour - only after login
 useEffect(() => {
 const user = getCurrentUser();
 const shouldShowTour = user && !isAuthPage && !hasCompletedTour();

 if (shouldShowTour) {
 // Small delay to let page render
 const timer = setTimeout(() => setIsActive(true), 1000);
 return () => clearTimeout(timer);
 } else {
 setIsActive(false);
 }
 }, [location.pathname, isAuthPage]);

 // Find and highlight target element
 useEffect(() => {
 if (!isActive || !step?.target) {
 setTargetRect(null);
 return;
 }

 const findTarget = () => {
 const element = document.querySelector(step.target!);
 if (element) {
 const rect = element.getBoundingClientRect();
 setTargetRect(rect);
 // Scroll into view if needed
 element.scrollIntoView({ behavior: "smooth", block: "center"});
 } else {
 setTargetRect(null);
 }
 };

 findTarget();
 // Re-check on resize
 window.addEventListener("resize", findTarget);
 return () => window.removeEventListener("resize", findTarget);
 }, [isActive, step]);

 const handleNext = useCallback(() => {
 if (isLastStep) {
 completeTour();
 setIsActive(false);
 onComplete?.();
 } else {
 setCurrentStep((s) => s + 1);
 }
 }, [isLastStep, onComplete]);

 const handlePrev = useCallback(() => {
 setCurrentStep((s) => Math.max(0, s - 1));
 }, []);

 const handleSkip = useCallback(() => {
 completeTour();
 setIsActive(false);
 onComplete?.();
 }, [onComplete]);

 if (!isActive || !step) return null;

 // Calculate tooltip position
 const getTooltipStyle = (): React.CSSProperties => {
 if (!targetRect) {
 // Center on screen for steps without target
 return {
 position: "fixed",
 top: "50%",
 left: "50%",
 transform: "translate(-50%, -50%)",
 };
 }

 const padding = 12;
 const tooltipWidth = 320;
 const tooltipHeight = 180;

 let top = 0;
 let left = 0;

 switch (step.position) {
 case"top":
 top = targetRect.top - tooltipHeight - padding;
 left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
 break;
 case"bottom":
 top = targetRect.bottom + padding;
 left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
 break;
 case"left":
 top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
 left = targetRect.left - tooltipWidth - padding;
 break;
 case"right":
 top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
 left = targetRect.right + padding;
 break;
 default:
 top = targetRect.bottom + padding;
 left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
 }

 // Keep within viewport
 if (left < padding) left = padding;
 if (left + tooltipWidth > window.innerWidth - padding) {
 left = window.innerWidth - tooltipWidth - padding;
 }
 if (top < padding) top = padding;
 if (top + tooltipHeight > window.innerHeight - padding) {
 top = window.innerHeight - tooltipHeight - padding;
 }

 return {
 position: "fixed",
 top,
 left,
 width: tooltipWidth,
 };
 };

 return (
 <>
 {/* Overlay */}
 <div className="fixed inset-0 z-[60] bg-slate-900/60 transition-opacity"/>

 {/* Highlight cutout for target element */}
 {targetRect && (
 <div
 className="fixed z-[61] rounded-lg ring-4 ring-role-primary ring-offset-4 ring-offset-transparent"style={{
 top: targetRect.top - 4,
 left: targetRect.left - 4,
 width: targetRect.width + 8,
 height: targetRect.height + 8,
 boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.6)",
 }}
 />
 )}

 {/* Tooltip */}
 <div
 className="z-[62] rounded-xl bg-white p-5 shadow-2xl"style={getTooltipStyle()}
 >
 {/* Progress indicator */}
 <div className="mb-4 flex items-center gap-1.5">
 {TOUR_STEPS.map((_, index) => (
 <div
 key={index}
 className={`h-1.5 rounded-full transition-all ${
 index === currentStep
 ?"w-6 bg-role-primary": index < currentStep
 ?"w-1.5 bg-emerald-50":"w-1.5 bg-slate-200"}`}
 />
 ))}
 </div>

 {/* Content */}
 <h3 className="text-base font-semibold text-slate-900">
 {step.title}
 </h3>
 <p className="mt-2 text-sm text-slate-600">
 {step.content}
 </p>

 {/* Actions */}
 <div className="mt-5 flex items-center justify-between">
 <button
 onClick={handleSkip}
 className="text-sm text-slate-500 hover:text-slate-700">
 Skip tour
 </button>
 <div className="flex items-center gap-2">
 {!isFirstStep && (
 <button
 onClick={handlePrev}
 className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
 Back
 </button>
 )}
 <button
 onClick={handleNext}
 className="rounded-lg bg-gradient-brand px-4 py-1.5 text-sm font-medium text-white shadow-sm transition hover:shadow-md">
 {isLastStep ?"Get Started":"Next"}
 </button>
 </div>
 </div>
 </div>
 </>
 );
}

/**
 * RestartTourButton - Button to restart the tour from settings
 */
export function RestartTourButton() {
 const [showTour, setShowTour] = useState(false);

 const handleRestart = () => {
 resetTour();
 setShowTour(true);
 };

 return (
 <>
 <button
 onClick={handleRestart}
 className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
 <svg
 className="h-4 w-4"fill="none"viewBox="0 0 24 24"stroke="currentColor"strokeWidth={2}
 >
 <path
 strokeLinecap="round"strokeLinejoin="round"d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
 </svg>
 Restart Tour
 </button>
 {showTour && <OnboardingTour onComplete={() => setShowTour(false)} />}
 </>
 );
}

export default OnboardingTour;

